import { useCallback, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CreateViewFromViewObjectModal } from 'customer-data-views-management/createView/components/CreateViewModal';
import { Metrics } from 'framework-listing-lib/internal/metrics';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';
import { useViewUpdateState } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import useViewForm from '../hooks/useViewForm';
import { useViews } from '../providers/ViewsProvider';
import { convertCustomerDataViewToView } from '../utils/mapper';
import { alertFailure, alertSuccess } from 'framework-listing-lib/utils/alerts';
import useApplicationId from '../../hooks/useApplicationId';
import { safeParseJSON } from '../../../utils/json';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { jsx as _jsx } from "react/jsx-runtime";
export default function CreateViewModal({
  formType,
  namespace,
  objectTypeId,
  onClose,
  onCreate
}) {
  const applicationId = useApplicationId();
  const buildViewRequest = useViewForm();
  const [, setViewUpdateState] = useViewUpdateState();
  const {
    trackCrmTableInteraction
  } = useCrmObjectTypeTracking();
  const {
    updateViewsCache
  } = useViews();
  const handleSuccess = useCallback(({
    createdView
  }) => {
    var _view$state$mLangGrou, _view$state;
    const view = convertCustomerDataViewToView(createdView);
    /**
     * If we reach this point, it means that the view has been created and it only exists in
     * customer-data-views-management.
     * In that case, we need to update our internal cache for subsequent view changes & updates.
     */
    updateViewsCache(existingCache => Object.assign({}, existingCache, {
      results: [...existingCache.results, Object.assign({}, view, {
        /**
         * customer-data-views-management does not send the view type in the response
         * and FLL automatically assumes that the view is a DEFAULT view (which is wrong).
         */
        type: 'STANDARD'
      })]
    }));
    Metrics.counter(formType === 'CLONE_WITH_CHANGES' ? 'clone-view-with-changes-success' : 'create-new-view-success-v4', {
      application_id: applicationId
    }).increment();
    if (formType === 'CREATE_NEW') {
      trackCrmTableInteraction({
        action: 'create-view'
      });
    }
    setViewUpdateState({
      changedCardProperties: false,
      changedColumns: false,
      changedFilters: false,
      changedSort: false,
      changedUIMode: false,
      changedQuickFilterPropertyNames: false,
      mLangGroup: {
        changed: false,
        showAll: (_view$state$mLangGrou = (_view$state = view.state) === null || _view$state === void 0 || (_view$state = _view$state.mLangGroup) === null || _view$state === void 0 ? void 0 : _view$state.showAll) !== null && _view$state$mLangGrou !== void 0 ? _view$state$mLangGrou : false
      }
    });

    /**
     * Scroll table back to 0,0 to start fresh with the newly created view.
     */
    const frameworkDataTable = document.querySelector('[data-test-id="framework-data-table"]');
    if (frameworkDataTable && frameworkDataTable.parentElement) {
      frameworkDataTable.parentElement.scrollTo(0, 0);
    }
    alertSuccess({
      message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "frameworkListingLib.viewTabs.alerts.viewCreate.success.message",
        options: {
          name: createdView.name
        }
      })
    });
    onCreate(view.id);
  }, [applicationId, formType, onCreate, setViewUpdateState, trackCrmTableInteraction, updateViewsCache]);
  const handleFailure = useCallback(() => {
    Metrics.counter(formType === 'CLONE_WITH_CHANGES' ? 'clone-view-with-changes-failure' : 'create-new-view-failure-v4', {
      application_id: applicationId
    }).increment();
    alertFailure({
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "frameworkListingLib.viewTabs.alerts.viewCreate.failure.message"
      }),
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "frameworkListingLib.viewTabs.alerts.viewCreate.failure.title"
      })
    });
  }, [applicationId, formType]);
  const viewObject = useMemo(() => {
    const viewRequest = buildViewRequest(formType);
    return {
      name: viewRequest.name,
      objectTypeId,
      private: viewRequest.private,
      namespaceName: namespace,
      // Different TS types between FLL and customer-data-views-management
      columns: safeParseJSON(viewRequest.columns, []),
      state: safeParseJSON(viewRequest.state, {
        order: 1,
        sortKey: 'hs_created_at'
      }),
      filterGroups: safeParseJSON(viewRequest.filterGroups, []),
      quickFilters: safeParseJSON(viewRequest.quickFilters, []),
      quickFilterProperties: safeParseJSON(viewRequest.quickFilterProperties, [])
    };
  }, [formType, buildViewRequest, namespace, objectTypeId]);
  return /*#__PURE__*/_jsx(CreateViewFromViewObjectModal, {
    viewObject: viewObject,
    strategy: formType === 'CLONE_WITH_CHANGES' ? 'CLONE_EXISTING' : 'CREATE_NEW',
    onSuccess: handleSuccess,
    onFailure: handleFailure,
    onClose: onClose
  });
}